import React, { FC, memo, useState } from 'react';
import classNames from 'classnames';

import Chip from '@zvuk-b2b/react-uikit/ui/Chip';
import IconX from '@zvuk-b2b/react-uikit/ui/icons/X';

import { TEXTS } from '../texts';
import { DATA_TEST_IDS, EMAIL_REGEXP, EMAIL_SEPARATOR_REGEXP } from '../consts';
import { ExternalReportRecipientsInputProps } from './types';

import styles from './index.module.scss';

const getPreparedEmails = (rawValue: string) =>
  rawValue
    .split(EMAIL_SEPARATOR_REGEXP)
    .map((email) => email.trim())
    .filter(Boolean);

const getEmailsWithoutDuplicate = (emails: string[]) => {
  const usedEmails = new Set<string>();

  return emails.filter((email) => {
    const normalizedEmail = email.toLowerCase();

    if (usedEmails.has(normalizedEmail)) {
      return false;
    }

    usedEmails.add(normalizedEmail);

    return true;
  });
};

const ExternalReportRecipientsInput: FC<ExternalReportRecipientsInputProps> = ({ isVisible, emails, onChange }) => {
  const [value, setValue] = useState('');

  if (!isVisible) {
    return null;
  }

  const addEmails = (rawValue: string) => {
    const nextEmails = getPreparedEmails(rawValue);

    if (nextEmails.length === 0) {
      return;
    }

    onChange(getEmailsWithoutDuplicate([...emails, ...nextEmails]));
    setValue('');
  };

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ' ' || event.key === ',' || event.key === ';') {
      event.preventDefault();
      addEmails(value);
    }

    if (event.key === 'Backspace' && !value) {
      onChange(emails.slice(0, -1));
    }
  };

  const onInputPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedValue = event.clipboardData.getData('text');

    if (getPreparedEmails(pastedValue).length > 1) {
      event.preventDefault();
      addEmails(pastedValue);
    }
  };

  const onRemoveEmail = (email: string) => {
    onChange(emails.filter((currentEmail) => currentEmail !== email));
  };

  const extensions = emails.map((email, index) => {
    const isInvalid = !EMAIL_REGEXP.test(email);

    return (
      <Chip
        key={email}
        elementId={`chip_ad_campaign_report_external_email_${email}`}
        dataTestId={DATA_TEST_IDS.EXTERNAL_RECIPIENT_CHIP(index)}
        className={classNames(styles.chip, isInvalid && styles.chipInvalid)}
        contentClassName={styles.chipContent}
        adornmentRight={
          <button
            type="button"
            data-testid={DATA_TEST_IDS.EXTERNAL_RECIPIENT_CHIP_REMOVE_BUTTON(index)}
            className={styles.chipRemoveButton}
            aria-label={TEXTS.EXTERNAL_RECIPIENT_REMOVE_LABEL(email)}
            onClick={() => onRemoveEmail(email)}>
            <IconX />
          </button>
        }>
        {email}
      </Chip>
    );
  });

  return (
    <div
      className={styles.container}
      data-testid={DATA_TEST_IDS.EXTERNAL_RECIPIENTS}>
      <label
        className={styles.label}
        htmlFor={DATA_TEST_IDS.EXTERNAL_RECIPIENTS_INPUT}
        data-testid={DATA_TEST_IDS.EXTERNAL_RECIPIENTS_LABEL}>
        {TEXTS.EXTERNAL_RECIPIENTS_LABEL}
      </label>

      <div
        className={styles.inputContainer}
        data-testid={DATA_TEST_IDS.EXTERNAL_RECIPIENTS_INPUT_CONTAINER}>
        {extensions}
        <div
          className={styles.inputWrapper}
          data-testid={DATA_TEST_IDS.EXTERNAL_RECIPIENTS_INPUT_WRAPPER}>
          <input
            id={DATA_TEST_IDS.EXTERNAL_RECIPIENTS_INPUT}
            data-testid={DATA_TEST_IDS.EXTERNAL_RECIPIENTS_INPUT}
            type="email"
            value={value}
            placeholder={emails.length === 0 ? TEXTS.EXTERNAL_RECIPIENTS_PLACEHOLDER : ''}
            autoComplete="off"
            className={styles.input}
            onChange={(event) => setValue(event.target.value)}
            onBlur={() => addEmails(value)}
            onKeyDown={onInputKeyDown}
            onPaste={onInputPaste}
          />
        </div>
      </div>
    </div>
  );
};

export default memo(ExternalReportRecipientsInput);
